import { transformDirection } from './_multilingual.js';

/*************************************
 * Carousel Initialization
 *************************************/

/*
  This script sets up carousels on the webpage using a class-based approach. Features include navigation through arrow buttons and dot indicators.

  The Carousel class provides methods to initialize, update, and manage carousel instances.
*/

class Carousel {
  constructor(baseClassName, carouselIndex, infiniteLoop = false, transitionDuration = 500, autoplayIntervalDuration = null) {
    this.baseClassName = baseClassName;
    this.carouselIndex = carouselIndex;
    this.infiniteLoop = infiniteLoop;
    this.transitionDuration = transitionDuration; // Set the transition duration
    this.autoplayIntervalDuration = autoplayIntervalDuration; // Set the autoplay interval duration
    this.autoplay = autoplayIntervalDuration !== null; // Autoplay is enabled if autoplayIntervalDuration is set
    this.selector = `.${this.baseClassName}__slider[data-carousel-index="${this.carouselIndex}"]`;
    this.carousel = document.querySelector(this.selector);
    this.track = this.carousel.querySelector(`.${this.baseClassName}__track`);
    this.progressBar = this.carousel.querySelector(`.${this.baseClassName}__progress-bar`); // Progress bar element
    this.carouselWidth = this.carousel.querySelector(`.${this.baseClassName}__element`).offsetWidth;
    this.carouselItems = [];
    this.itemWidth = 0;
    this.marginInlineEnd = 0;
    this.numItems = 0;
    this.numVisibleItems = 0;
    this.numDuplicates = 0;
    this.initialOffset = 0;
    this.currentPosition = 0;
    this.currentSlideIndex = 1;
    this.lastWindowWidth = window.innerWidth;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchStartY = 0;
    this.touchEndY = 0;
    this.isSwiping = false;
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragEndX = 0;
    this.startTranslateX = 0;
    this.maxPosition = 0;
    this.autoplayInterval = null;
    this._loopJumpTimeout = null;
    this.isEnabled = false;
    this.hasDragged = false;

    // Bind the visibility change handler for pausing/resuming autoplay
    this.handleVisibilityChangeBound = this.handleVisibilityChange.bind(this);

    this.init();
  }

  clearEventListeners() {
    if (this.prevButton) {
      this.prevButton.removeEventListener('click', this.handlePrevButtonClick);
    }
    if (this.nextButton) {
      this.nextButton.removeEventListener('click', this.handleNextButtonClick);
    }
    window.removeEventListener('resize', this.debounceResize);

    this.carousel.removeEventListener('touchstart', this.handleTouchStart);
    this.carousel.removeEventListener('touchmove', this.handleTouchMove);
    this.carousel.removeEventListener('touchend', this.handleTouchEnd);

    this.carousel.removeEventListener('mousedown', this.handlePointerDown);
    window.removeEventListener('mousemove', this.handlePointerMove);
    window.removeEventListener('mouseup', this.handlePointerUp);

    if (this.handleClick) {
      this.carousel.removeEventListener('click', this.handleClick, true);
    }

    // Remove visibility change event listener
    document.removeEventListener('visibilitychange', this.handleVisibilityChangeBound);

    // Clear autoplay interval
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  init() {
    this.isEnabled = true;
    this.clearEventListeners(); // Clear existing event listeners
    if (!this.carousel) {
      console.error(`Carousel not found for index ${this.carouselIndex}`);
      return;
    }
    this.carouselItems = Array.from(this.carousel.querySelectorAll(`.${this.baseClassName}__item:not(.clone)`));
    if (this.carouselItems.length <= 1) {
      return; // Exit initialization if there are 1 or fewer items
    }
    this.carousel.classList.remove('initialised');
    this.carousel.removeAttribute('style');
    this.setupCarouselItems();

    // Disable infinite loop if not enough items to loop
    if (this.carouselItems.length <= this.numVisibleItems) {
      this.infiniteLoop = false;
    }

    this.setupNavigationArrows();
    this.bindEvents();
    this.updateCarousel(); // Initial update to set the correct state
    setTimeout(() => {
      this.carousel.classList.add('initialised');
      this.carousel.style.height = `${this.carousel.offsetHeight}px`;
    });
    // Set up autoplay if enabled
    if (this.autoplay) {
      this.setupAutoplay();
    }
  }

  enable() {
    if (this.isEnabled) return; // Prevent double initialization
    this.isEnabled = true;

    this.clearEventListeners(); // Clear existing event listeners
    this.carouselItems = Array.from(this.carousel.querySelectorAll(`.${this.baseClassName}__item:not(.clone)`));
    if (this.carouselItems.length <= 1) return;

    this.setupCarouselItems();
    this.setupNavigationArrows();
    this.bindEvents();
    this.updateCarousel();
    this.carousel.classList.add('initialised');

    if (this.autoplay) this.setupAutoplay();
  }

  disable() {
    if (!this.isEnabled) return; // Prevent double disable
    this.isEnabled = false;

    this.clearEventListeners(); // Remove event listeners
    this.carousel.classList.remove('initialised');
    this.carousel.style.transform = '';
    this.carousel.style.height = '';
    if (this.autoplayInterval) clearInterval(this.autoplayInterval); // Stop autoplay
  }

  setupCarouselItems() {
    this.carouselItems = Array.from(this.carousel.querySelectorAll(`.${this.baseClassName}__item:not(.clone)`));
    this.itemWidth = this.calculateItemWidth();
    this.marginInlineEnd = parseFloat(getComputedStyle(this.carouselItems[0]).marginInlineEnd);
    this.numItems = this.carouselItems.length;
    this.numVisibleItems = Math.floor((this.carouselWidth + this.marginInlineEnd) / (this.itemWidth + this.marginInlineEnd));
    this.initialOffset = this.setupInfiniteLoop() * transformDirection();
  }

  calculateItemWidth() {
    let firstCarouselItem = Array.from(this.carouselItems).find(item => !item.classList.contains('active'));
    if (!firstCarouselItem) {
      firstCarouselItem = this.carouselItems[0];
    }
    if (!firstCarouselItem) {
      console.error(`Carousel width not definable for index ${this.carouselIndex}`);
      return;
    }

    const itemStyle = getComputedStyle(firstCarouselItem);
    let width = parseFloat(itemStyle.width);
    if (width > this.carouselWidth) {
      width = this.carouselWidth;
    }
    return width;
  }

  setupInfiniteLoop() {
    if (!this.infiniteLoop) return 0;
    if (this.numVisibleItems >= this.carouselItems.length) return 0;

    this.carouselItems = Array.from(this.carousel.querySelectorAll(`.${this.baseClassName}__item`));

    if (this.carousel.querySelectorAll(`.${this.baseClassName}__item`).length > this.carousel.querySelectorAll(`.${this.baseClassName}__item:not(.clone)`).length) {
      this.dispatchCarouselUpdateEvent();
      this.numDuplicates = this.carousel.querySelectorAll(`.${this.baseClassName}__item.clone`).length / 2;
      return -(this.itemWidth + this.marginInlineEnd) * this.numDuplicates;
    }

    this.numDuplicates = this.numVisibleItems + 1;

    // Add the last `numDuplicates` items to the start of the track
    for (let i = 1; i <= this.numDuplicates; i++) {
      const lastItemClone = this.carouselItems[this.carouselItems.length - i].cloneNode(true);
      lastItemClone.classList.remove('active');
      lastItemClone.classList.add('clone');
      this.track.insertBefore(lastItemClone, this.track.firstChild);
      this.carouselItems.unshift(lastItemClone); // Add clone to the front of the carousel items array
    }

    // Add the first `numDuplicates` items to the end of the track
    for (let i = 1; i <= this.numDuplicates; i++) {
      const firstItemClone = this.carouselItems[this.numDuplicates + i - 1].cloneNode(true);
      firstItemClone.classList.remove('active');
      firstItemClone.classList.add('clone');
      this.track.appendChild(firstItemClone);
      this.carouselItems.push(firstItemClone); // Add clone to the carousel items array
    }

    return -(this.itemWidth + this.marginInlineEnd) * this.numDuplicates; // initial offset for clones
  }

  setupNavigationArrows() {
    this.prevButton = this.carousel.querySelector(`.${this.baseClassName}__prev`);
    this.nextButton = this.carousel.querySelector(`.${this.baseClassName}__next`);

    // Event handlers defined as class properties to manage and unbind easily
    this.handlePrevButtonClick = (e) => {
      e.preventDefault();
      const slideNumber = this.currentSlideIndex - 1;
      this.navigateToSlide(slideNumber);

      // Clear autoplay interval
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    };

    this.handleNextButtonClick = (e) => {
      e.preventDefault();
      const slideNumber = this.currentSlideIndex + 1;
      this.navigateToSlide(slideNumber);

      // Clear autoplay interval
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    };
  }

  setupAutoplay() {
    if (this.autoplay) {
      this.autoplayInterval = setInterval(() => {
        const slideNumber = this.currentSlideIndex + 1;
        this.navigateToSlide(slideNumber);
      }, this.autoplayIntervalDuration); // Use the autoplay interval duration variable
    }
  }

  bindEvents() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.handlePrevButtonClick);
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.handleNextButtonClick);
    }

    // Debouncing the resize event
    this.debounceResize = this.debounce(() => this.handleResize(), 250);
    window.addEventListener('resize', this.debounceResize);

    // Touch events handling
    this.handleTouchStart = (e) => {
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.isSwiping = false;
      this.hasDragged = false;
      this.startTranslateX = this.currentPosition; // Capture the start position of the carousel
      this.track.style.transitionDuration = '0ms';
      // this.track.style.transitionTimingFunction = 'linear';
    };

    this.handleTouchMove = (e) => {
      const touchCurrentX = e.touches[0].clientX;
      const touchCurrentY = e.touches[0].clientY;

      const deltaX = touchCurrentX - this.touchStartX;
      const deltaY = touchCurrentY - this.touchStartY;

      if (!this.isSwiping) {
        this.isSwiping = Math.abs(deltaX) > Math.abs(deltaY);
      }

      if (this.isSwiping) {
        e.preventDefault(); // Prevent scrolling
        this.touchEndX = touchCurrentX;
        this.hasDragged = true;
        this.updateSwipePosition(deltaX);
      }
    };

    this.handleTouchEnd = (e) => {
      if (this.isSwiping) {
        this.isSwiping = false;
        this.handleSwipeGesture();
        // Reset drag flag after gesture is complete
        setTimeout(() => {
          this.hasDragged = false;
        }, 50);
      }
    };

    this.carousel.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    this.carousel.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.carousel.addEventListener('touchend', this.handleTouchEnd, { passive: false });

    this.handlePointerDown = (e) => {
      // Only react to primary button
      if (e.button !== 0) return;
      this.dragStartX  = e.clientX;
      this.isDragging  = true;
      this.hasDragged = false;
      this.startTranslateX = this.currentPosition;
      this.track.style.transitionDuration = '0ms';

      // Prevent images/links inside slides from being dragged
      e.preventDefault();
    };

    this.handlePointerMove = (e) => {
      if (!this.isDragging) return;
      const deltaX = e.clientX - this.dragStartX;
      this.hasDragged = true;
      this.updateSwipePosition(deltaX);
    };

    this.handlePointerUp = (e) => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.dragEndX = e.clientX;
      // Re-use the same gesture handler as touch
      this.touchStartX = this.dragStartX;
      this.touchEndX   = this.dragEndX;
      this.handleSwipeGesture();
      // Reset drag flag after gesture is complete
      setTimeout(() => {
        this.hasDragged = false;
      }, 50);
    };

    this.carousel.addEventListener('mousedown', this.handlePointerDown);
    window.addEventListener('mousemove', this.handlePointerMove);
    window.addEventListener('mouseup', this.handlePointerUp);

    // Prevent clicks when dragging has occurred
    this.handleClick = (e) => {
      if (this.hasDragged) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    this.carousel.addEventListener('click', this.handleClick, true);

    // Add visibility change event listener to pause/resume autoplay
    document.addEventListener('visibilitychange', this.handleVisibilityChangeBound);
  }

  // New method: Pause autoplay when the page is hidden and resume when visible.
  handleVisibilityChange() {
    if (document.hidden) {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
        this.autoplayInterval = null;
      }
    } else {
      // Restart autoplay if enabled and not already running
      if (this.autoplay && !this.autoplayInterval) {
        this.setupAutoplay();
      }
    }
  }

  updateSwipePosition(deltaX) {
    const directionMultiplier = transformDirection();
    const newPosition = this.startTranslateX + (deltaX * directionMultiplier);
    this.track.style.transform = `translateX(${newPosition}px)`;
  }

  handleSwipeGesture() {
    // Width of one logical slide (card width + its gap)
    const slideWidth = this.itemWidth + this.marginInlineEnd;

    // How far the pointer actually travelled (positive → next slide in LTR)
    const directionMultiplier = transformDirection();
    const swipeDistance =
      (this.touchStartX - this.touchEndX) * directionMultiplier;

    // Ignore micro-drags shorter than a quarter slide
    const threshold = slideWidth * 0.25;
    if (Math.abs(swipeDistance) < threshold) {
      this.updateCarousel();
      return;
    }

    // Convert distance to whole-slide steps (always ≥ 1)
    let slidesMoved = Math.round(Math.abs(swipeDistance) / slideWidth);
    if (slidesMoved === 0) slidesMoved = 1;
    if (swipeDistance < 0) slidesMoved *= -1;   // drag right → negative

    const targetIndex = this.currentSlideIndex + slidesMoved;
    this.navigateToSlide(targetIndex);

    // Stop autoplay after manual interaction
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  updateProgressBar() {
    if (this.progressBar) {
      const progressPercentage = ((this.currentSlideIndex) / (this.numItems - this.numVisibleItems + 1)) * 100;
      this.progressBar.style.width = `${progressPercentage}%`;
    }
  }

  updateCarousel(animate = false) {
    // Set transition duration based on whether we want animation.
    this.track.style.transitionDuration = animate ? `${this.transitionDuration}ms` : '0ms';
  
    // Calculate and update the current position.
    this.currentPosition = this.initialOffset + (-(this.itemWidth + this.marginInlineEnd) * (this.currentSlideIndex - 1)) * transformDirection();
    this.track.style.transform = `translateX(${this.currentPosition}px)`;
  
    // Update navigation arrows, progress bar, etc.
    this.updateNavigationArrows();
    this.updateProgressBar();
  
    // Update the active slide class.
    this.carouselItems.forEach((item, index) => {
      const currentIndex = this.currentSlideIndex + this.numDuplicates - 1;
      if (index == currentIndex) {
        item.classList.add('active');
      } else {
        item.classList.remove('active');
      }
    });
  }

  updateNavigationArrows() {
    this.maxPosition = this.initialOffset + (-(this.itemWidth * this.numItems + this.marginInlineEnd * (this.numItems - 1)) + this.carouselWidth) * transformDirection();

    if (this.prevButton) {
      if (this.numVisibleItems >= this.carouselItems.length) {
        this.nextButton.classList.add('disabled');
      } else if (
          (this.currentPosition >= 0 && !this.infiniteLoop && transformDirection() == 1) || 
          (this.currentPosition <= 0 && !this.infiniteLoop && transformDirection() == -1)
        ) {
        this.prevButton.classList.add('disabled');
      } else {
        this.prevButton.classList.remove('disabled');
      }
    }
    if (this.nextButton) {
      if (this.numVisibleItems >= this.carouselItems.length) {
        this.nextButton.classList.add('disabled');
      } else if (
        (this.currentPosition <= this.maxPosition && !this.infiniteLoop && transformDirection() == 1) || 
        (this.currentPosition >= this.maxPosition && !this.infiniteLoop && transformDirection() == -1)
      ) {
        this.nextButton.classList.add('disabled');
      } else {
        this.nextButton.classList.remove('disabled');
      }
    }
  }

  navigateToSlide(index) {
    if (this.infiniteLoop) {
      // Use animation for the normal slide.
      this.currentSlideIndex = Math.max(0 - (this.numVisibleItems + 1), Math.min(this.numItems + (this.numVisibleItems + 1), index));
      this.updateCarousel(true);
  
      // Check for a clone slide, then jump instantly after the transition.
      if (this.currentSlideIndex <= 0) {
        this._scheduleJump(() => {
          this.currentSlideIndex = this.numItems + this.currentSlideIndex;
          this.updateCarousel(false);
        });
      } else if (this.currentSlideIndex >= this.numItems + 1) {
        this._scheduleJump(() => {
          console.log(this.currentSlideIndex);
          this.currentSlideIndex = this.currentSlideIndex - this.numItems;
          this.updateCarousel(false);
        });
      }
    } else {
      this.currentSlideIndex = Math.max(1, Math.min(this.numItems - this.numVisibleItems + 1, index));
      this.updateCarousel(true);
    }
  
    this.dispatchCarouselUpdateEvent();
  }  

  dispatchCarouselUpdateEvent() {
    const event = new CustomEvent('carouselUpdate', {
      detail: { index: this.currentSlideIndex, totalItems: this.numItems, visibleItems: this.numVisibleItems },
      bubbles: true // This allows the event to bubble up through the DOM
    });
    this.carousel.dispatchEvent(event);
  }

  // Function to handle carousel update on window resize
  handleResize() {
    // Check if the window width has actually changed
    if (window.innerWidth !== this.lastWindowWidth) {
      this.lastWindowWidth = window.innerWidth; // Update the lastWindowWidth variable
      this.init()
    }
  };

  debounce(func, delay) {
    let timer;
    return () => {
      clearTimeout(timer);
      timer = setTimeout(func, delay);
    };
  }
  
  _scheduleJump(fn) {
    if (this._loopJumpTimeout !== null) {
      clearTimeout(this._loopJumpTimeout);   // cancel previous jump
    }

    this._loopJumpTimeout = window.setTimeout(() => {
      fn();                                   // perform the instant jump
      this._loopJumpTimeout = null;           // no timeout pending now
    }, this.transitionDuration - 20);
  }
}

// Create a global object to store carousel instances
window.carouselInstances = {};

// Initializes all carousels on page load
window.addEventListener('pageshow', () => {
  const carousels = document.querySelectorAll('.carousel__slider');
  carousels.forEach((carousel, index) => {
    carousel.setAttribute('data-carousel-index', index);

    const carouselInstance = new Carousel('carousel', index, true, 1000);

    // Store the instance in a global object, using the index as the key
    window.carouselInstances[`carousel-${index}`] = carouselInstance;
  });

  const galleryCarousels = document.querySelectorAll('.gallery-carousel__slider');
  galleryCarousels.forEach((carousel, index) => {
    carousel.setAttribute('data-carousel-index', index);

    const carouselInstance = new Carousel('gallery-carousel', index, true, 1000, 3500);

    // Store the instance in a global object, using the index as the key
    window.carouselInstances[`gallery-carousel-${index}`] = carouselInstance;
  });

  const cardsCarousels = document.querySelectorAll('.cards-carousel__slider');
  cardsCarousels.forEach((carousel, index) => {
    carousel.setAttribute('data-carousel-index', index);

    const carouselInstance = new Carousel('cards-carousel', index, true, 1000, 3500);

    // Store the instance in a global object, using the index as the key
    window.carouselInstances[`cards-carousel-${index}`] = carouselInstance;
  });

  const logosCarousels = document.querySelectorAll('.logos-carousel__slider');
  logosCarousels.forEach((carousel, index) => {
    carousel.setAttribute('data-carousel-index', index);
    const carouselInstance = new Carousel('logos-carousel', index, true, 3000, 3000);

    // Store the instance in a global object, using the index as the key
    window.carouselInstances[`logos-carousel-${index}`] = carouselInstance;
  });
});